import React, { useState, useEffect } from 'react';
import './PrivateLogin.css';
import { FaKey, FaArrowLeft, FaSpinner } from 'react-icons/fa';
import Dashboard from '../rust/Dashboard';
import MobileDashboard from '../rust/MobileDashboard';
import ApexDashboard from '../apex/ApexDashboard';
import { useTranslation } from 'react-i18next';

// Komponente für die animierten Punkte
const AnimatedEllipsis = () => {
  const [dots, setDots] = useState('');
  
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => (prev.length < 3 ? prev + '.' : ''));
    }, 500);
    return () => clearInterval(interval);
  }, []);
  
  return <span>{dots}</span>;
};

// Favicon-Funktion
const changeFavicon = (href) => {
  const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
  link.type = 'image/x-icon';
  link.rel = 'shortcut icon';
  link.href = href;
  document.getElementsByTagName('head')[0].appendChild(link);
};

const PrivateLogin = () => {
  const { t } = useTranslation();
  const [productKey, setProductKey] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resultMessage, setResultMessage] = useState("");
  const [resultColor, setResultColor] = useState("");
  const [isValidKey, setIsValidKey] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [keyStatus, setKeyStatus] = useState("");
  const [expiryValue, setExpiryValue] = useState(null);
  const [downloadLink, setDownloadLink] = useState("");
  const [apkDownloadLink, setApkDownloadLink] = useState("");
  const [keyType, setKeyType] = useState(""); // "rust" oder "apex"

  // Setze Standard-Favicon für PrivateLogin
  useEffect(() => {
    changeFavicon('/favicon.ico');
  }, []);

  // Einfach jeden eingegebenen Wert übernehmen
  const handleInputChange = (e) => {
    setProductKey(e.target.value);
  };

  const handleFocus = () => {
    setProductKey("");
  };

  // Enter zum Verifizieren, sonst nichts weiter
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleVerify();
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    setResultMessage("");
    
    // Bestimme Key-Typ basierend auf dem Format
    const isApexKey = productKey.startsWith("AP-");
    const isMobileKey = productKey.startsWith("Mo-");
    let currentKeyType = "rust";
    if (isApexKey) {
      currentKeyType = "apex";
    } else if (isMobileKey) {
      currentKeyType = "mobile";
    }
    setKeyType(currentKeyType);
    
    const apiUrl = "https://eaglescript.space:8443/check-key"; // <- Direkter Port
    const headers = { "Content-Type": "application/json" };
    const payload = { key: productKey };

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      let message = "";
      let color = "";
      let valid = false;

      if (!response.ok) {
        message = t('key_not_valid');
        color = "red";
      } else {
        const data = await response.json();
        if (data.status === "activated" || data.status === "not activated") {
          message = t('key_is_valid');
          color = "#CAF881"; // Immer grün für "Key is valid" Text
          valid = true;
          setKeyStatus(data.status);
          setExpiryValue(
            data.status === "not activated"
              ? data.duration
              : data.remaining_seconds
          );
          setDownloadLink(data.download_link);
          setApkDownloadLink(data.apk_download_link);
        } else {
          message = t('key_not_valid');
          color = "red";
        }
      }

      // Mindestens 2 Sekunden Spinner anzeigen
      setTimeout(() => {
        setResultMessage(message);
        setResultColor(color);
        setIsValidKey(valid);
        setVerifying(false);
        if (valid) {
          setTimeout(() => setLoggedIn(true), 2000);
        }
      }, 2000);

    } catch (err) {
      setTimeout(() => {
        setResultMessage(t('key_not_valid'));
        setResultColor("red");
        setIsValidKey(false);
        setVerifying(false);
      }, 2000);
    }
  };

  if (loggedIn) {
    // Zeige entsprechendes Dashboard basierend auf Key-Typ
    if (keyType === "apex") {
      return (
        <ApexDashboard 
          productKey={productKey} 
          keyStatus={keyStatus} 
          expiryValue={expiryValue} 
          downloadLink={downloadLink}
        />
      );
    } else if (keyType === "mobile") {
      return (
        <MobileDashboard 
          productKey={productKey} 
          keyStatus={keyStatus} 
          expiryValue={expiryValue} 
          downloadLink={downloadLink}
          apkDownloadLink={apkDownloadLink}
        />
      );
    } else {
      return (
        <Dashboard 
          productKey={productKey} 
          keyStatus={keyStatus} 
          expiryValue={expiryValue} 
          downloadLink={downloadLink}
        />
      );
    }
  }

  return (
    <div className="private-login-page" id="private-login">
      <div className="private-login-container">
        <h2>{t('enter_product_key')}</h2>
        <input
          type="text"
          className="product-key-input"
          placeholder={t('enter_product_key_placeholder')}
          value={productKey}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
        />
        <div className="api-response" style={{ margin: '5px 0 15px', textAlign: 'center' }}>
          {verifying ? (
            <div className="verifying" style={{ color: 'white' }}>
              <FaSpinner className="spinner" style={{ marginRight: '0.5rem' }} />
              <span>{t('verifying_code')}</span>
            </div>
          ) : (
            resultMessage && (
              isValidKey ? (
                <div>
                  <p style={{ margin: 0, color: resultColor }}>{resultMessage}</p>
                  <p style={{ margin: 0, color: resultColor, fontWeight: '900', fontSize: '1em' }}>
                    {t('forwarding_to_dashboard')}<AnimatedEllipsis />
                  </p>
                </div>
              ) : (
                <p style={{ margin: 0, color: resultColor }}>{resultMessage}</p>
              )
            )
          )}
        </div>
        <div className="private-login-buttons">
          <button className="private-login-button verify-button" onClick={handleVerify}>
            <FaKey className="icon" />
            <span>{t('verify')}</span>
          </button>
          <a href="/" className="private-login-button back-button">
            <FaArrowLeft className="icon" />
            <span>{t('back_to_homepage')}</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default PrivateLogin;